import { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import {
  buildAllProducts,
  mergeTaxonomyFromApi,
  normalizeCategoryList,
  normalizeProductSlugKeys,
} from './catalogNormalize';

const EMPTY_PRODUCTS = {};
const CATALOG_CACHE_KEY = 'saloni_catalog_cache_v2';
const CATALOG_CACHE_TTL_MS = 5 * 60 * 1000;

function isValidCatalogPayload(payload) {
  const mergedProducts = payload?.mergedProducts;
  return (
    mergedProducts &&
    typeof mergedProducts === 'object' &&
    !Array.isArray(mergedProducts) &&
    Object.keys(mergedProducts).length > 0
  );
}

function readCatalogCache() {
  try {
    const raw = sessionStorage.getItem(CATALOG_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - parsed.ts > CATALOG_CACHE_TTL_MS) return null;
    if (!isValidCatalogPayload(parsed.payload)) return null;
    return parsed.payload;
  } catch {
    return null;
  }
}

function writeCatalogCache(payload) {
  try {
    sessionStorage.setItem(CATALOG_CACHE_KEY, JSON.stringify({ ts: Date.now(), payload }));
  } catch {
    /* quota / private mode */
  }
}

function catalogStateFromPayload(payload) {
  if (!isValidCatalogPayload(payload)) {
    return {
      products: EMPTY_PRODUCTS,
      categories: [],
      allProducts: [],
      catalogDbActive: false,
      dataSource: 'none',
      error: null,
      loading: true,
    };
  }
  const { mergedProducts, base, dataSource } = payload;
  return {
    products: mergedProducts,
    categories: normalizeCategoryList(base, mergedProducts),
    allProducts: buildAllProducts(mergedProducts),
    catalogDbActive: true,
    dataSource: dataSource || 'mysql',
    error: null,
    loading: false,
  };
}

function initialCatalogState() {
  const cached = readCatalogCache();
  if (!cached) {
    return {
      products: EMPTY_PRODUCTS,
      categories: [],
      allProducts: [],
      catalogDbActive: false,
      dataSource: 'none',
      error: null,
      loading: true,
    };
  }
  return catalogStateFromPayload(cached);
}

function parseCatalogResponse(text) {
  const trimmed = String(text || '')
    .replace(/^\uFEFF/, '')
    .trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    const i = trimmed.indexOf('{');
    const j = trimmed.lastIndexOf('}');
    if (i >= 0 && j > i) {
      return JSON.parse(trimmed.slice(i, j + 1));
    }
    throw new Error('invalid-json');
  }
}

function isMysqlCatalogPayload(data) {
  return (
    data &&
    data.products &&
    typeof data.products === 'object' &&
    !Array.isArray(data.products) &&
    (data.ok === true ||
      data.ok === 1 ||
      data.ok === 'true' ||
      data.success === true ||
      data.success === 1)
  );
}

export function useProducts() {
  const location = useLocation();
  const { ready, isAdmin, token } = useAuth();
  const loadGenRef = useRef(0);
  const boot = initialCatalogState();
  const hasCatalogRef = useRef(boot.catalogDbActive);

  const [allProducts, setAllProducts] = useState(boot.allProducts);
  const [products, setProducts] = useState(boot.products);
  const [categories, setCategories] = useState(boot.categories);
  const [catalogDbActive, setCatalogDbActive] = useState(boot.catalogDbActive);
  const [dataSource, setDataSource] = useState(boot.dataSource);
  const [loading, setLoading] = useState(boot.loading);
  const [error, setError] = useState(boot.error);

  const adminCatalogRoute = location.pathname.startsWith('/admin');

  const loadCatalog = useCallback(async ({ silent = false } = {}) => {
    const gen = ++loadGenRef.current;
    const showLoading = !silent && !hasCatalogRef.current;
    if (showLoading) {
      setLoading(true);
    }
    setError(null);

    const isStale = () => gen !== loadGenRef.current;

    try {
      const useAdminCatalog =
        adminCatalogRoute &&
        ready &&
        isAdmin &&
        String(token || '').trim() !== '';
      const catalogUrl = useAdminCatalog ? '/api/admin_catalog.php' : '/api/catalog.php';
      const catalogFetchOpts = useAdminCatalog
        ? {
            cache: 'no-store',
            headers: {
              Authorization: `Bearer ${String(token || '').trim()}`,
              'X-Admin-Session': String(token || '').trim(),
            },
          }
        : { cache: 'no-store' };

      const r = await fetch(catalogUrl, catalogFetchOpts);
      const text = await r.text();
      let data = null;
      try {
        data = parseCatalogResponse(text);
      } catch {
        throw new Error(
          !r.ok
            ? `${catalogUrl} returned HTTP ${r.status}. Run npm run php:serve with npm start.`
            : `Invalid JSON from ${catalogUrl}. Check PHP warnings in the response.`
        );
      }

      if (!isMysqlCatalogPayload(data)) {
        const msg =
          (data && typeof data.error === 'string' && data.error) ||
          (data && !r.ok ? `Catalog API HTTP ${r.status}.` : null) ||
          'Catalog API did not return MySQL data. Configure includes/db_config.php and run catalog migrations.';
        throw new Error(msg);
      }

      const mergedProducts = normalizeProductSlugKeys(data.products);
      const base = mergeTaxonomyFromApi(data.categories, mergedProducts);
      normalizeCategoryList(base, mergedProducts).forEach((c) => {
        if (!Array.isArray(mergedProducts[c.slug])) {
          mergedProducts[c.slug] = [];
        }
      });

      if (!isStale()) {
        const payload = {
          mergedProducts,
          base,
          dataSource: 'mysql',
        };
        const next = catalogStateFromPayload(payload);
        setProducts(next.products);
        setCategories(next.categories);
        setAllProducts(next.allProducts);
        setCatalogDbActive(next.catalogDbActive);
        setDataSource(next.dataSource);
        setError(null);
        writeCatalogCache(payload);
        hasCatalogRef.current = true;
      }
    } catch (e) {
      if (!isStale()) {
        if (!hasCatalogRef.current) {
          setProducts(EMPTY_PRODUCTS);
          setCategories([]);
          setAllProducts([]);
          setCatalogDbActive(false);
          setDataSource('none');
        }
        setError(e.message || 'Could not load catalog from database.');
      }
    } finally {
      if (!isStale()) {
        setLoading(false);
      }
    }
  }, [adminCatalogRoute, ready, isAdmin, token]);

  useEffect(() => {
    loadCatalog({ silent: hasCatalogRef.current });
  }, [loadCatalog]);

  useEffect(() => {
    const onCatalogUpdated = () => {
      loadCatalog({ silent: hasCatalogRef.current });
    };
    window.addEventListener('saloni-catalog-updated', onCatalogUpdated);
    return () => window.removeEventListener('saloni-catalog-updated', onCatalogUpdated);
  }, [loadCatalog]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        loadCatalog({ silent: hasCatalogRef.current });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadCatalog]);

  return {
    allProducts,
    products,
    categories,
    catalogDbActive,
    dataSource,
    loading,
    error,
    refetch: loadCatalog,
  };
}
